import { Provider } from "jotai";
import { useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import tools from "../../../../../../helpers/tools";
import EmailLink from "../../../../../common/CommentList/common/EmailLink/EmailLink";
import PhoneLink from "../../../../../common/CommentList/common/PhoneLink/PhoneLink";
import CompanyInfo from "../../../../../common/CompanyInfo/CompanyInfo";
import DocumentList from "../../../../../common/DocumentList/DocumentList";
import LabelValuePair from "../../../../../common/LabelValuePair/LabelValuePair";
import { PanelSectionTitle } from "../../../../../common/layout/StyledComponents/StyledComponents";
import LegalFormValue from "../../../../../common/LegalFormValue/LegalFormValue";
import ProductInfos from "../../../../../common/ProductInfos/ProductInfos";
import { LeadPageContext } from "../../../useLeads";
import useLeadItem from "../useLeadItem";

const effectif = (n) => (n === 0 || n === null || n === undefined ? "0" : tools.truenumberWithSpaces(n));
const euros = (n) => `${tools.truenumberWithSpaces(n)} €`;

// Documents du produit à montrer, selon le produit et l'avancement du lead
// (mêmes règles que l'ancien volet « Infos et documents »).
const documentsProduit = (lead) => {
  if (lead.productId === "PJ") return { titre: "Documents PJ", ref: "Quotation" };
  if (lead.productId === "MRP" && lead.isFinalized) return { titre: "Documents MRP", ref: "Quotation" };
  if (lead.productId === "CYBER" && lead.isFinalized) return { titre: "Documents CYBER", ref: "Quotation" };
  if (lead.productId === "domi") return { titre: "Documents DOMI", ref: "Quote" };
  return null;
};

// « Infos et documents » d'un lead entreprise ou DOMI : documents du produit
// et de la société, réponses du questionnaire (avec « Modifier mes réponses »
// pour MRP et RC Pro), société et établissement. Les fichiers se chargent à
// l'ouverture de l'onglet, une seule fois par lead.
const InfosLead = () => {
  const { productCategory } = useContext(LeadPageContext);
  const { lead, actions, isSearchingFiles } = useLeadItem();
  const fichiers = Array.isArray(lead.files) ? lead.files : [];
  const docs = documentsProduit(lead);
  const nomClient = productCategory === "DOMI" ? tools.getFullName(lead.user) : lead.client && lead.client.company;

  useEffect(() => {
    actions.chargerFichiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);

  return (
    <div className="la-infos">
      {docs && (
        <DocumentList
          title={docs.titre}
          files={fichiers.filter((f) => f[0].entity.ref === docs.ref)}
          isLoading={isSearchingFiles}
          companyName={nomClient}
          productName={lead.productId}
        />
      )}
      {lead.client && (
        <DocumentList
          title="Documents de la société"
          files={fichiers.filter((f) => f[0].entity.ref === "Client")}
          isLoading={isSearchingFiles}
          companyName={lead.client.company}
          productName={lead.productId}
        />
      )}
      <Provider>
        <ProductInfos productId={lead.productId} productData={lead.productData} />
        {["MRP", "RCPRO"].includes(lead.productId) && lead.client && lead.establishment && (
          <Link
            target="_blank"
            to={`/modify-answers/${lead.client.id}/${lead.productId}/${lead.establishment.id}/${lead.quotationId}`}
            className="la-bouton-lien la-infos__modifier"
          >
            <i className="fa fa-edit" aria-hidden="true" />
            Modifier mes réponses
          </Link>
        )}
      </Provider>
      {lead.client && (
        <section className="la-infos__section" aria-label="Société">
          <PanelSectionTitle>Société</PanelSectionTitle>
          <CompanyInfo
            isHeadOffice
            isOpened
            company={lead.client.company}
            siret={lead.client.siret}
            infos={[
              tools.getFullName(lead.userCustomer),
              <PhoneLink phone={lead.userCustomer && lead.userCustomer.phone} />,
              <EmailLink email={lead.userCustomer && lead.userCustomer.email} />,
              <LabelValuePair label="Forme juridique" value={<LegalFormValue legalForm={lead.client.legalForm} />} />,
              <LabelValuePair label="Adresse" value={tools.getFullAddress(lead.client.location1)} />,
              <LabelValuePair label="CA" value={euros(lead.client.turnover)} />,
              <LabelValuePair label="Immatriculée le" value={tools.formatDate(lead.client.registrationAt)} />,
              <LabelValuePair label="Effectif" value={effectif(lead.client.workforce)} />,
            ]}
          />
          {lead.establishment && !lead.establishment.isHeadOffice && (
            <CompanyInfo
              className="la-infos__etablissement"
              isHeadOffice={false}
              isOpened
              company={lead.establishment.company}
              siret={lead.establishment.siret}
              infos={[
                <LabelValuePair label="Adresse" value={tools.getFullAddress(lead.establishment.location)} />,
                <LabelValuePair label="CA" value={euros(lead.establishment.turnover)} />,
                <LabelValuePair label="Immatriculée le" value={tools.formatDate(lead.establishment.registrationAt)} />,
                <LabelValuePair label="Effectif" value={effectif(lead.establishment.workforce)} />,
              ]}
            />
          )}
        </section>
      )}
    </div>
  );
};

export default InfosLead;
